import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronDown, ChevronUp } from 'lucide-react';
import { productAPI } from '../../services/api';
import ProductCard from '../../components/shop/ProductCard';

const CATEGORIES = ['floral','woody','oriental','fresh','citrus','gourmand','aromatic','chypre'];
const GENDERS = ['men','women','unisex'];
const CONCENTRATIONS = ['EDT','EDP','Parfum','EDC','Cologne'];
const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
];

const FilterSection = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: '20px', marginBottom: '20px' }}>
      <button onClick={() => setOpen(!open)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', color: 'var(--white)', fontSize: '11px', letterSpacing: '0.2em', fontWeight: 600, marginBottom: open ? '14px' : 0 }}>
        {title}{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && children}
    </div>
  );
};

const CollectionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';
  const gender = searchParams.get('gender') || '';
  const concentration = searchParams.get('concentration') || '';
  const sort = searchParams.get('sort') || 'newest';
  const search = searchParams.get('search') || '';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort };
      if (category) params.category = category;
      if (gender) params.gender = gender;
      if (concentration) params.concentration = concentration;
      if (search) params.search = search;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      const { data } = await productAPI.getAll(params);
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [page, category, gender, concentration, sort, search, minPrice, maxPrice]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  const clearFilters = () => setSearchParams({});

  const hasFilters = category || gender || concentration || minPrice || maxPrice;

  const Filters = () => (
    <div style={{ width: '240px', flexShrink: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '11px', letterSpacing: '0.25em', fontWeight: 600 }}>FILTERS</h3>
        {hasFilters && (
          <button onClick={clearFilters} style={{ fontSize: '10px', color: 'var(--gold)', background: 'none', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <X size={10} /> CLEAR
          </button>
        )}
      </div>

      <FilterSection title="CATEGORY">
        {CATEGORIES.map(c => (
          <label key={c} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
            <input type="radio" name="category" checked={category === c} onChange={() => setParam('category', category === c ? '' : c)}
              style={{ accentColor: 'var(--gold)' }} />
            <span style={{ fontSize: '12px', color: 'var(--white-dim)', textTransform: 'capitalize' }}>{c}</span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="GENDER">
        {GENDERS.map(g => (
          <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
            <input type="radio" name="gender" checked={gender === g} onChange={() => setParam('gender', gender === g ? '' : g)}
              style={{ accentColor: 'var(--gold)' }} />
            <span style={{ fontSize: '12px', color: 'var(--white-dim)', textTransform: 'capitalize' }}>{g}</span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="CONCENTRATION">
        {CONCENTRATIONS.map(c => (
          <label key={c} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
            <input type="radio" name="concentration" checked={concentration === c} onChange={() => setParam('concentration', concentration === c ? '' : c)}
              style={{ accentColor: 'var(--gold)' }} />
            <span style={{ fontSize: '12px', color: 'var(--white-dim)' }}>{c}</span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="PRICE RANGE">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="number" placeholder="Min" value={minPrice}
            onChange={e => setParam('minPrice', e.target.value)}
            style={{ width: '80px', padding: '8px', fontSize: '12px', border: '1px solid var(--border)', background: 'var(--dark-2)', color: 'var(--white)', borderRadius: '2px' }} />
          <span style={{ color: 'var(--white-dim)' }}>—</span>
          <input type="number" placeholder="Max" value={maxPrice}
            onChange={e => setParam('maxPrice', e.target.value)}
            style={{ width: '80px', padding: '8px', fontSize: '12px', border: '1px solid var(--border)', background: 'var(--dark-2)', color: 'var(--white)', borderRadius: '2px' }} />
        </div>
      </FilterSection>
    </div>
  );

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'var(--dark)', padding: '48px 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <p style={{ fontSize: '10px', letterSpacing: '0.3em', color: 'var(--gold)', marginBottom: '8px' }}>
            {search ? `SEARCH: "${search.toUpperCase()}"` : 'BROWSE'}
          </p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 400 }}>
            {category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Our Collection'}
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--white-dim)', marginTop: '8px' }}>{total} fragrance{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <button onClick={() => setFiltersOpen(!filtersOpen)} className="mobile-filter-btn"
            style={{ display: 'none', alignItems: 'center', gap: '8px', background: 'var(--dark-2)', border: '1px solid var(--border)', color: 'var(--white)', padding: '10px 16px', fontSize: '12px', letterSpacing: '0.1em' }}>
            <SlidersHorizontal size={14} /> FILTERS
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '11px', color: 'var(--white-dim)', letterSpacing: '0.1em' }}>SORT BY</span>
            <select value={sort} onChange={e => setParam('sort', e.target.value)}
              style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', color: 'var(--white)', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}>
              {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '40px' }}>
          {/* Sidebar filters — desktop */}
          <div className="desktop-filters"><Filters /></div>

          {/* Products */}
          <div style={{ flex: 1 }}>
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '24px' }}>
                {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: '380px' }} />)}
              </div>
            ) : products.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--white-dim)' }}>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: '28px', marginBottom: '12px' }}>No fragrances found</p>
                <p style={{ fontSize: '13px' }}>Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '24px' }}>
                  {products.map(p => <ProductCard key={p._id} product={p} />)}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '48px' }}>
                    {[...Array(pages)].map((_, i) => (
                      <button key={i} onClick={() => setParam('page', i + 1)}
                        style={{ width: '36px', height: '36px', border: `1px solid ${page === i + 1 ? 'var(--gold)' : 'var(--border)'}`, background: page === i + 1 ? 'var(--gold)' : 'transparent', color: page === i + 1 ? 'var(--black)' : 'var(--white)', fontSize: '12px', cursor: 'pointer', transition: 'all 0.3s' }}>
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .desktop-filters { display: none !important; }
          .mobile-filter-btn { display: flex !important; }
        }
        .desktop-filters { display: block; }
      `}</style>
    </div>
  );
};

export default CollectionPage;
